import { ReactElement } from 'react';
import { Field, useFormikContext } from 'formik';
import { TileRadioAccessible, IndividualFormNominate, CompanyFormNominate, DateInput, FileUploadGroupAccessible } from '@components';
import { NominationInfoDisplayObject } from '@services/FormFieldConfig';
import { iNominationFormValues } from '@services/NominationService';
import { tile } from '@interfaces/interfaces';
import { FormType } from '@constants/forms';
import gridStyles from '@pages/forms/Grid.module.scss';
import styles from './NominationStep7.module.scss';

function NominationStep7(): ReactElement {
  const { values }: { values: iNominationFormValues; } = useFormikContext<iNominationFormValues>();
  const tSVBoatingMaringOffenceStr = values.IsMarineOffence || values.IsTsvOffence ? 'vessel' : 'vehicle';
  const IndividualOption = (): ReactElement => (
    <>
      <div>Person</div>
    </>
  );

  const OrganisationOption = (): ReactElement => (
    <>
      <div>Company</div>
    </>
  );

  const TILE_DATA: tile[] = [
    {
      body: <IndividualOption />,
      value: 'Person',
    },
    {
      body: <OrganisationOption />,
      value: 'Company',
    },
  ];
  const YesValue = (): ReactElement => (
    <>
      <div>Yes</div>
    </>
  );

  const NoValue = (): ReactElement => (
    <>
      <div>No</div>
    </>
  );

  const IsTransfered: tile[] = [
    {
      body: <YesValue />,
      value: 'true',
    },
    {
      body: <NoValue />,
      value: 'false',
    },
  ];

  return (
    <div className={gridStyles.form}>
      <div className="vfH2">The {tSVBoatingMaringOffenceStr} was sold or permanently disposed of before the offence</div>
      <p className={styles.details}>
        <b>We need proof of the sale/disposal of the {tSVBoatingMaringOffenceStr}, along with the buyer's details.</b>
      </p>
      <div className={gridStyles.gridColFull}>
        <div className={styles.col}>
          <Field
            name="TransferedVicRoads"
            title={`Has the ${tSVBoatingMaringOffenceStr} been transferred with VicRoads?`}
            component={TileRadioAccessible}
            tileData={IsTransfered}
            orientationRow
            isMandatory
          />
        </div>
        <p>You should provide evidence showing that the {tSVBoatingMaringOffenceStr} was sold or disposed of prior to the offence.</p>
        <p>Visit <a href="https://www.vicroads.vic.gov.au/registration/buy-sell-or-transfer-a-vehicle" target="_blank" rel="noopener noreferrer">VicRoads</a> for information about transferring {tSVBoatingMaringOffenceStr} ownership.</p>
      </div>
      <div className={gridStyles.gridColHalf}>
        <Field name="DateSellOrDispose" component={DateInput} minDate={new Date(1900, 1, 1)} title={`What date did you sell or dispose of the ${tSVBoatingMaringOffenceStr}?`} isMandatory maxDate={new Date()} />
      </div>
      <>
        <span style={{ marginTop: '1em' }}><b>Please attach evidence of the sale or disposal</b> (optional)</span>
        <span>maximum of {values.FileUploadMaxAttachmentsConfig} files; each file should be less than {values.FileUploadSizeConfig}MB</span>
        <FileUploadGroupAccessible
          uploadAReport="EvidenceSoldOrDisposeNameField"
          uploadFieldArrayName="EvidenceSoldOrDispose"
          whoPrepared="EvidenceSoldOrDisposeProvider"
          briefDescription="EvidenceSoldOrDisposeFilesDescription"
          fileSizeErrorField="EvidenceSoldOrDisposeSizeErrorHandle"
          fileUploadFailErrorField="EvidenceSoldOrDisposeFileUploadErrorHandle"
          UploadFileSectionError="EvidenceSoldOrDisposeFileSectionError"
          fileSizelimit={values.FileUploadSizeConfig.toString()}
          totalFileLimit={values.FileUploadMaxAttachmentsConfig}
          hasPrepared={false}
          hasDescribe={false}
          formType={FormType.NOMINATION} />
      </>
      <Field
        name="WhoAreYouNominatingSoldDispose"
        title="Who are you nominating?"
        component={TileRadioAccessible}
        tileData={TILE_DATA}
        wrapTiles
        isMandatory
        info={{
          caption: 'Your nominee must be over 18 years of age.',
          tileValue: 'individual'
        }}
        orientationRow />
      {values.WhoAreYouNominatingSoldDispose && (
        <div className={styles.requiredDetails}>
          {values.WhoAreYouNominatingSoldDispose === 'Person' && (
            <p className={styles.bold}>Details of the person you are nominating</p>
          )}
          {values.WhoAreYouNominatingSoldDispose === 'Company' && (
            <>
              <p className={styles.bold}>Details of the company you are nominating</p>
            </>
          )}
        </div>
      )}
      {values.WhoAreYouNominatingSoldDispose === 'Person' && <IndividualFormNominate displayObject={NominationInfoDisplayObject} currentStepNo="step7" />}
      {values.WhoAreYouNominatingSoldDispose === 'Company' && <CompanyFormNominate displayObject={NominationInfoDisplayObject} currentStepNo="step7" />}
      {values.WhoAreYouNominatingSoldDispose === 'Person' && <p><br />If this Nomination Statement is accepted, the fine will be withdrawn and re issued to the nominated driver for them to pay. <br /><br /> The driver can reject your nomination. If this happens, the fine will be re issued back to you</p>}
      {values.WhoAreYouNominatingSoldDispose === 'Company' && <p>If your Nomination Statement is accepted, the fine will be withdrawn and reissued to the responsible company. <br /><br /> The company nominated can reject the nomination. If this happens, the fine will be reissued to you.</p>}
    </div>
  );
}

export default NominationStep7;
